<template>
  <div>
     <v-row>
      <v-col cols="12" md="4">
         <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="460"
        ></v-skeleton-loader>
        <v-card v-else class="mb-4 scrollable-card" style="height: 460px; overflow-y: auto;">
          <!-- The tabs row also holds the PNG export of the ACTIVE tab's donut -->
          <div class="d-flex align-center">
            <v-tabs v-model="donutTab" color="primary" bg-color="surface" density="comfortable" class="flex-grow-1" style="min-width: 0;">
              <v-tab value="responsible">{{ $t('responsibleParty') }}</v-tab>
              <v-tab value="assigned">{{ $t('assignedTeam') }}</v-tab>
            </v-tabs>
            <v-btn
              class="rdrive-chart-export mx-2 flex-shrink-0"
              icon="mdi-download"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="!issuesLoaded"
              @click="downloadDonutTab"
            />
          </div>
          <v-card-text class="pa-0">
            <BaseDonutChart
              ref="responsiblePartyDonutRef"
              v-if="dataLoaded"
              v-show="donutTab === 'responsible'"
              class="pa-4"
              bare
              :series-name="$t('responsibleParty')"
              :exportFilename="$t('responsibleParty') + '.png'"
              :exportTitle="$t('responsibleParty')"
              :dataProvider="responsiblePartyDonut.getResponsiblePartyDistribution"
              :onSelect="responsiblePartyDonut.setResponsiblePartyFilter"
              :filterContext="filterContext"
              :chartFontColor="chartFontColor"
              :dataLoaded="issuesLoaded"
            />
            <BaseDonutChart
              ref="assignedTeamDonutRef"
              v-if="dataLoaded && donutTab === 'assigned'"
              class="pa-4"
              bare
              :series-name="$t('assignedTeam')"
              :exportFilename="$t('assignedTeam') + '.png'"
              :exportTitle="$t('assignedTeam')"
              :dataProvider="assignedTeamDonut.getAssignedTeamDistribution"
              :onSelect="assignedTeamDonut.setAssignedTeamFilter"
              :filterContext="filterContext"
              :chartFontColor="chartFontColor"
              :dataLoaded="issuesLoaded"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <StackedHorizontalBarChart
          :title="$t('priorityBreakdown')"
          :exportFilename="$t('priorityBreakdown') + '.png'"
          v-bind="priorityChart"
          :filterContext="filterContext"
          :chartFontColor="chartFontColor"
          :dataLoaded="issuesLoaded"
          skipEmptyLevels
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="460"
        ></v-skeleton-loader>
        <v-card v-else class="mb-4 scrollable-card" style="height: 460px; overflow-y: auto;">
          <!-- The tabs row also holds the PNG export of the ACTIVE tab's donut -->
          <div class="d-flex align-center">
            <v-tabs v-model="donutTab2" color="primary" bg-color="surface" density="comfortable" class="flex-grow-1" style="min-width: 0;">
              <v-tab value="controller">{{ $t('controllers') }}</v-tab>
              <v-tab value="createdBy">{{ $t('createdBy') }}</v-tab>
            </v-tabs>
            <v-btn
              class="rdrive-chart-export mx-2 flex-shrink-0"
              icon="mdi-download"
              variant="text"
              size="small"
              density="comfortable"
              :disabled="!issuesLoaded"
              @click="downloadDonutTab2"
            />
          </div>
          <v-card-text class="pa-0">
            <BaseDonutChart
              ref="controllerDonutRef"
              v-if="dataLoaded"
              v-show="donutTab2 === 'controller'"
              class="pa-4"
              bare
              :series-name="$t('teamDistribution')"
              :exportFilename="$t('controllers') + '.png'"
              :exportTitle="$t('controllers')"
              :dataProvider="controllerDonut.getTeamDistribution"
              :onSelect="controllerDonut.setTeamFilter"
              :filterContext="filterContext"
              :chartFontColor="chartFontColor"
              :dataLoaded="issuesLoaded"
            />
            <BaseDonutChart
              ref="createdByDonutRef"
              v-if="dataLoaded && donutTab2 === 'createdBy'"
              class="pa-4"
              bare
              :series-name="$t('teamDistribution')"
              :exportFilename="$t('createdBy') + '.png'"
              :exportTitle="$t('createdBy')"
              :dataProvider="createdByDonut.getTeamDistribution"
              :onSelect="createdByDonut.setTeamFilter"
              :drill="{ dataProvider: createdByDonut.getUserDistribution, onSelect: createdByDonut.setUserFilter, seriesName: $t('userDistribution') }"
              :filterContext="filterContext"
              :chartFontColor="chartFontColor"
              :dataLoaded="issuesLoaded"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <StackedHorizontalBarChart
          :title="$t('issueBreakdown')"
          :exportFilename="$t('issueBreakdown') + '.png'"
          v-bind="categoryChart"
          :filterContext="filterContext"
          :chartFontColor="chartFontColor"
          :dataLoaded="issuesLoaded"
          skipEmptyLevels
        />
      </v-col>
      <v-col cols="12" md="6">
        <div>
          <!-- Resolution Time: package BaseBoxPlotChart + useResolutionTimeChart (IA_ABSORPTION_PLAN
               12d); the status picker, the filter button and its date-range dialog are the config's -->
          <BaseBoxPlotChart
            :title="$t('resolutionTimeAnalysis')"
            :exportFilename="$t('resolutionTimeAnalysis') + '.png'"
            v-bind="resolutionTimeChart"
            :filterContext="issueAndHistoryContexts"
            :dataLoaded="resolutionTimeReady"
            :color="resolutionTimeColor"
            :reloadKey="selectedStatus"
            navigation="arrows"
            skipEmptyLevels
          >
            <template #controls>
              <v-combobox
                :model-value="selectedStatus"
                @update:model-value="setStatus"
                :selected="selectedStatus"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="Select status"
                variant="outlined"
                density="compact"
                hide-selected
                hide-details
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    :color="item.raw.color"
                    :text="item.raw.title"
                    size="small"
                    variant="flat"
                    label
                  ></v-chip>
                </template>

                <template v-slot:item="{ props: itemProps, item }">
                  <v-list-item 
                    v-bind="itemProps"
                    class="d-flex"
                  >
                    <template v-slot:title>
                      <v-chip
                        :color="item.raw.color"
                        :text="item.raw.title"
                        size="small"
                        variant="flat"
                        label
                      ></v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-combobox>
              <v-btn
                icon="mdi-filter"
                size="small"
                variant="text"
                @click="settingsDialog = true"
                class="mr-2"
              ></v-btn>
            </template>
          </BaseBoxPlotChart>

          <!-- The filter button's settings dialog: the status-history date range -->
          <v-dialog v-model="settingsDialog" max-width="600" persistent>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ $t('chartSettings') }}</span>
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <v-label class="text-caption">{{ $t('dateRangeSelected') }}</v-label>
                    <div class="text-body-2 mt-1 mb-3">
                      {{ getDateFromIndex(tempStatusHistoryRange[0], true) }} - {{ getDateFromIndex(tempStatusHistoryRange[1], true) }}
                    </div>
                    <v-range-slider
                      v-model="tempStatusHistoryRange"
                      :min="0"
                      :max="statusHistoryDates.length - 1"
                      :step="1"
                      :disabled="!dataLoaded || statusHistoryDates.length === 0"
                      class="mt-4"
                    >
                      <template v-slot:thumb-label="{ value }">
                        {{ getDateFromIndex(value) }}
                      </template>
                    </v-range-slider>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="grey"
                  variant="text"
                  @click="cancelSettings"
                >
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  @click="applySettings"
                >
                  {{ $t('apply') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <BaseTimeSeriesChart
          mode="cumulative"
          bandOrder="asc"
          :title="$t('burnup.title')"
          :infoText="$t('burnup.tooltip')"
          :exportFilename="$t('rdriveBurndownChart') + '.png'"
          :showToolbar="showChartDownload"
          :filterContext="issueAndHistoryContexts"
          :dataLoaded="dataLoaded"
          v-bind="burndownChart"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <BaseTimeSeriesChart
          mode="periodic"
          seriesType="bar"
          :title="$t('velocityOverTime')"
          :infoText="$t('velocityTooltip')"
          :exportFilename="$t('rdriveVelocityChart') + '.png'"
          :showToolbar="showChartDownload"
          :filterContext="issueAndHistoryContexts"
          :dataLoaded="dataLoaded"
          v-bind="velocityChart"
        />
      </v-col>
    </v-row>

    <!-- Warning Alert -->
    <v-row v-if="showNoActivityWarning">
      <v-col>
        <v-alert
          type="warning"
          variant="tonal"
          border="start"
          closable
        >
          {{ $t('noActivityWarning', { 
            startDate: formatDateForDisplay(dateRange[0]), 
            endDate: formatDateForDisplay(dateRange[1]) 
          }) }}
        </v-alert>
      </v-col>
    </v-row>
    
  
    <!-- Items Data Table -->
    <v-row>
      <v-col cols="12" md="12">
        <IssuesGrid v-bind="issuesGrid" />
      </v-col>
    </v-row>
  </div>
</template>

<script src="./model.js"></script>
<style src="./style.css"></style>
